import { DOCUMENT } from '@angular/common';
import { Component, Inject, Input, OnDestroy, OnInit, Optional } from '@angular/core';
import { BlockActionType } from '@core/enums';
import { TableParams, ViewPart } from '@core/models';
import { Block } from '@core/models/block/block.model';
import { ApiInventoryService } from '@core/services/api/api-inventory.service';
import { ButtonHandlerService } from '@core/services/button-handler.service';
import { ComponentDependencyJunctionService } from '@core/services/component-dependency-junction.service';
import { SubSink } from '@core/utils/subsink';
import { PopupService } from '@modules/creator/components/popup/services/popup.service';
import { FormService } from '@modules/creator/components/special-block/forms/services/form.service';
import { AdvancedFiltersService } from '@shared/services/filters/advanced-filters.service';
import { FiltersService } from '@shared/services/filters/filters.service';
import { LocalFacetingService } from '@shared/services/filters/local-faceting.service';
import { AdvancedFilterData, UpdateAvailableFilters, UpdateAvailableFiltersReason } from '@shared/services/filters/types';
import { snakeCase } from 'lodash';
import { Observable, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, switchMap, tap } from 'rxjs/operators';

@Component({
  selector: 'app-advanced-filters',
  templateUrl: './advanced-filters.component.html',
  styleUrls: ['./advanced-filters.component.scss'],
  providers: [FormService, ComponentDependencyJunctionService],
  standalone: false,
})
export class AdvancedFiltersComponent implements OnInit, OnDestroy {
  showResetBlock = true;
  @Input() specialBlock: Block;
  private subSink = new SubSink();

  constructor(public formService: FormService,
              public filterService: FiltersService,
              public buttonHandlerService: ButtonHandlerService,
              private apiInventoryService: ApiInventoryService,
              private advancedFiltersService: AdvancedFiltersService,
              private popupService: PopupService,
              private localFacetingService: LocalFacetingService,
              @Inject(DOCUMENT) private document: Document,
              @Optional() public junctionService: ComponentDependencyJunctionService) {
    junctionService?.initFilterService(filterService);
  }

  get filtersBlock(): Block | undefined {
    return this.specialBlock.children.find(block => block.settings.viewPart === ViewPart.FILTERS_CONTENT);
  }

  get resetBlock(): Block | undefined {
    return this.specialBlock.children.find(block => block.settings.viewPart === ViewPart.RESET_CONTENT);
  }


  ngOnInit(): void {
    // Pobranie indeksu do lokalnego facetingu — pojedynczy request w tle.
    // Kolejne liczniki po kliknięciach są potem liczone w JS bez odwoływania
    // się do endpointu liczników per filtr.
    const lang = this.document.documentElement.lang || 'pl';
    this.subSink.sink = this.localFacetingService.loadIndex(lang).subscribe();

    // Liczby wyników per filtr odświeżamy po każdej akcji użytkownika.
    // Aktualizacje pochodzące z systemu (nie z akcji użytkownika) pomijamy.
    this.subSink.sink = this.advancedFiltersService.availableFiltersUpdated$
      .pipe(
        filter(updateValue => updateValue.reason !== UpdateAvailableFiltersReason.systemUpdate),
        switchMap(updateValue => this.getFiltersCounts(updateValue)),
      )
      .subscribe();
    this.advancedFiltersService.restoreAvailableFilters();
    const filters = this.advancedFiltersService.allFiltersValue;
    if (Object.keys(filters.data).length === 0) {
      this.subSink.sink = this.apiInventoryService.getInventoryFilters().subscribe(filters => {
        this.advancedFiltersService.initializeAvailableFilters(filters);
      });
    } else {
      if (this.shouldRefreshCountsOnInit(filters)) {
        this.subSink.sink = this.getFiltersCounts(filters).subscribe();
      }
    }
    if (!this.formService.formGroup.contains('extended_search')) {
      this.formService.addControl('extended_search', filters.extendedSearch);
    } else {
      this.formService.formGroup.get('extended_search')?.setValue(filters.extendedSearch);
    }
    if (!this.formService.formGroup.contains('search')) {
      this.formService.addControl('search', filters.searchTerm);
    } else {
      this.formService.formGroup.get('search')?.setValue(filters.searchTerm);
    }
    this.formService.formGroup.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
      )
      .subscribe(value => {
        this.advancedFiltersService.setFilters({
          extendedSearch: value.extended_search,
          searchTerm: value.search,
        }, UpdateAvailableFiltersReason.userAction);
      });

    // clear subscription
    this.subSink.sink = this.buttonHandlerService.click$
      .pipe(
        filter(({actionType}) => actionType === BlockActionType.CLEAR),
        tap(() => {
          this.advancedFiltersService.clearActiveFilters();
          // Serwis już ma puste search/extendedSearch — UI formGroup trzeba
          // zsynchronizować ręcznie (valueChanges samo się nie odpali).
          this.formService.formGroup.patchValue(
            {search: '', extended_search: false},
            {emitEvent: false},
          );
        }),
      ).subscribe();

    // search Subscription
    this.subSink.sink = this.buttonHandlerService.click$
      .pipe(
        filter(({actionType}) => actionType === BlockActionType.SUBMIT),
        tap(_ => this.advancedFiltersService.saveAvailableFilters()),
        tap(_ => this.advancedFiltersService.updateParamsForList()),
        tap(() => this.popupService.close()),
      ).subscribe();

    this.subSink.sink = this.popupService.onClose$
      .pipe(
        tap(_ => this.advancedFiltersService.restoreAvailableFilters()),
      ).subscribe();

    // search Subscription
    // this.subSink.sink = this.formService.keyboardEvent
    //   .pipe(
    //     filter(({code}) => ['NumpadEnter', 'Enter'].includes(code)),
    //     tap(_ => this.filterService.search(this.formService.formGroup.value)),
    //   ).subscribe();

    // Handle search field changes - reset extended_search when search is cleared
    // this.subSink.sink = this.formService.changed
    //   .pipe(
    //     filter(({formControlName}) => formControlName === 'search'),
    //     tap(({event}) => {
    //       // If search field is empty, reset extended_search checkbox
    //       if (!event || event === '') {
    //         this.formService.formGroup.patchValue({extended_search: false});
    //       }
    //     }),
    //   ).subscribe();
  }

  getFiltersCounts(updateValue: UpdateAvailableFilters): Observable<void> {
    const data = updateValue.data;
    const value = updateValue.dataForQueryCount;
    const getVal = (name: string) => {
      const val = value[name]?.map(v => v.id);
      return val?.length > 0 ? val : undefined;
    };

    const customFilterNames = Object.keys(data)
      .filter(name => name.startsWith('custom') && Array.isArray(data[name]));
    const activeFilters = this.advancedFiltersService.getUserFiltersValue(data);
    const baseFilters = this.advancedFiltersService.getInitialListParamsValue();
    const searchTermVal = updateValue.searchTerm;
    const searchTerm = searchTermVal ? searchTermVal.trim() : undefined;
    const extendedSearch = !!updateValue.extendedSearch;
    const lang = this.document.documentElement.lang || 'pl';
    const countsSource = searchTerm
      ? this.apiInventoryService.getListCategoryFilter(new TableParams({
        categories: getVal('categories'),
        materials: getVal('materials'),
        periods: getVal('periods'),
        types: getVal('types'),
        countries: getVal('countries'),
        styles: getVal('styles'),
        themes: getVal('themes'),
        artists: getVal('artists'),
        availability: getVal('availability'),
        inventory_statuses: getVal('inventoryStatuses'),
        ...customFilterNames.reduce((params, name) => {
          const val = getVal(name);
          if (val) {
            params[snakeCase(name)] = val;
          }
          return params;
        }, {}),
        searchTerm,
        extendedSearch,
        limit: -1,
        pageUrl: this.document.location.pathname,
      }))
      : (
        this.localFacetingService.isAvailable()
          ? of(true)
          : this.localFacetingService.loadIndex(lang)
      ).pipe(
        map(loaded => {
          if (!loaded && !this.localFacetingService.isAvailable()) {
            throw new Error('AdvancedFiltersComponent: facet-index nie jest dostępny');
          }

          return this.localFacetingService.computeFacets(activeFilters, baseFilters);
        }),
      );

    return countsSource.pipe(
      tap(counts => {
        const currentData = structuredClone(this.advancedFiltersService.getAvailableFiltersValue());
        const updateCounts = (name: string, countsNames: string | string[]) => {
          if (!data[name] || !currentData[name]) {
            return;
          }

          const countKeys = Array.isArray(countsNames) ? countsNames : [countsNames];
          const countsForFilter = countKeys
            .map(countsName => counts[countsName])
            .find((value): value is { id: number, count: number }[] => Array.isArray(value)) || [];
          currentData[name] = currentData[name].map(v => ({
            ...v,
            count: countsForFilter.find(c => c.id === v.id)?.count || 0,
          }));

          const propagateCountsToParents = () => {
            let hasChanges = true;
            while (hasChanges) {
              hasChanges = false;
              currentData[name] = currentData[name].map(item => {
                if (item.count === 0) {
                  const childrenSum = currentData[name]
                    .filter(child => child.parentId === item.id)
                    .reduce((sum, child) => sum + (child.count || 0), 0);
                  if (childrenSum > 0) {
                    hasChanges = true;
                    return {...item, count: childrenSum};
                  }
                }
                return item;
              });
            }
          };

          propagateCountsToParents();

          currentData[name] = currentData[name].map(v => ({
            ...v,
            disabled: v.count === 0,
          }));
        };

        updateCounts('categories', ['categoriesName', 'categoryName']);
        updateCounts('materials', 'materialsName');
        updateCounts('periods', 'periodsName');
        updateCounts('types', 'typesName');
        updateCounts('countries', ['countriesName', 'countrysName']);
        updateCounts('styles', 'stylesName');
        updateCounts('themes', 'themesName');
        updateCounts('artists', ['artistsName', 'artistName']);
        updateCounts('availability', 'availabilityName');
        updateCounts('inventoryStatuses', 'inventoryStatusesName');
        customFilterNames.forEach(name => updateCounts(name, `${name}Name`));
        queueMicrotask(() => {
          this.advancedFiltersService.setFilters({filters: currentData, _allCount: counts.count});
        });
      }),
      map(() => undefined as void),
    );
  }

  ngOnDestroy(): void {
    this.subSink.unsubscribe();
  }

  private shouldRefreshCountsOnInit(filters: UpdateAvailableFilters): boolean {
    if (filters._allCount === null) {
      return true;
    }

    const countableGroups = Object.values(filters.data).filter((value): value is AdvancedFilterData[] => (
      Array.isArray(value) && value.length > 0
    ));

    return filters._allCount > 0 &&
      countableGroups.length > 0 &&
      countableGroups.every(group => group.every(item => (item.count ?? 0) === 0));
  }
}
